import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";
import useValidateWorkflow from "./useValidateWorkflow";
import useErrorModal from "../useErrorModal";
import { firstInvalidPageIndex, sortPages, stepSlugs } from "../../services/dynamicForms/workflowAdapter";
import { ROUTES } from "../../constants/clientRoutes";
import { HTTP_STATUS_CODES } from "../../constants/api";
import { SEND_MONEY_WORKFLOW_STEPS } from "../../constants/enums";

// types
import type { SendMoneyReview, SendMoneyWorkflowValidationErrors } from "../../types/api/sendMoney";
import type {
  SendMoneyQuote,
  SendMoneyReviewLocationState,
  SendMoneyWorkflowLocationState,
} from "../../types/sendMoney";

/**
 * Continue from Transfer Details for a saved recipient: validate against the quote.
 * 200 → the review step with the review handed over; 226 → the workflow with the
 * returned pages, failing fields carrying their errors; 226 with no field error →
 * generic error modal. Other statuses are handled by useValidateWorkflow.
 *
 * Both handovers carry the quote that was validated, for the freshness check in
 * SendMoneyWorkflow.
 */
const useExistingRecipientContinue = () => {
  const history = useHistory();
  const { loading: validating, validateWorkflow } = useValidateWorkflow();
  const openErrorModal = useErrorModal();

  const goToReview = useCallback(
    (review: SendMoneyReview, quote: SendMoneyQuote) => {
      const state: SendMoneyReviewLocationState = {
        review,
        transactionContextId: quote.transactionContextId,
        quoteId: quote.quoteId,
      };
      history.push(generatePath(ROUTES.SEND_MONEY.WORKFLOW, { step: SEND_MONEY_WORKFLOW_STEPS.REVIEW }), state);
    },
    [history]
  );

  const goToWorkflow = useCallback(
    (response: SendMoneyWorkflowValidationErrors, quote: SendMoneyQuote) => {
      const state: SendMoneyWorkflowLocationState = {
        workflow: {
          pages: response.pages,
          quoteId: quote.quoteId,
          transactionContextId: quote.transactionContextId,
        },
        quoteId: quote.quoteId,
      };
      const [firstStep] = stepSlugs(sortPages(response.pages));
      history.push(generatePath(ROUTES.SEND_MONEY.WORKFLOW, { step: firstStep }), state);
    },
    [history]
  );

  const continueExistingRecipient = useCallback(
    async (quote: SendMoneyQuote) => {
      const response = await validateWorkflow({ quoteId: quote.quoteId });
      if (!response) return;

      const isValidationError = response.status === HTTP_STATUS_CODES.VALIDATION_ERROR;
      if (!isValidationError) {
        goToReview(response.result as SendMoneyReview, quote);
        return;
      }

      const validationErrors = response.result as SendMoneyWorkflowValidationErrors;

      // 226 with no field-level error: nothing to fix inline — generic error modal
      const hasFieldErrors = firstInvalidPageIndex(validationErrors.pages) !== -1;
      if (!hasFieldErrors) {
        openErrorModal();
        return;
      }

      goToWorkflow(validationErrors, quote);
    },
    [validateWorkflow, goToReview, goToWorkflow, openErrorModal]
  );

  return { validating, continueExistingRecipient };
};

export default useExistingRecipientContinue;